// Reliability display rules (Module 09 §3.9 / §4.1) — pure, unit-tested.
//
// The BAND is the one visual rule for a score chip, stated once so the list,
// the 360 header and every later surface (the composer's credit panel) cannot
// disagree about where amber begins: ≥ 80 green, 50–79 amber, < 50 red.
//
// The FORMULA text ("X of Y invoices on time → S") renders ONLY when the
// snapshot's formula inputs are present on the wire — §7 redacts them by
// OMISSION outside ga/fa/finance/sales-assigned, and a popover that printed
// "undefined of undefined" would be fabricating a redacted fact. The helper
// answers `undefined` and the caller renders nothing, which is the sales
// redaction rule everywhere: absence renders as absence, never as a dash
// pretending to be data.

export type ReliabilityBand = "ok" | "warn" | "bad";

/** §4.1's chip colour: green ≥ 80, amber 50–79, red < 50. */
export function reliabilityBand(score: number): ReliabilityBand {
  if (score >= 80) return "ok";
  if (score >= 50) return "warn";
  return "bad";
}

/** The wire fields the formula popover reads — all §7-omitted for
 * unprivileged roles, hence optional. */
export interface ReliabilityFormulaSource {
  readonly invoices_paid?: number | undefined;
  readonly invoices_paid_on_time?: number | undefined;
  readonly score: number;
}

export interface ReliabilityFormula {
  readonly onTime: number;
  readonly paid: number;
  readonly score: number;
}

/** The popover's interpolation inputs, or `undefined` when the §7 formula
 * inputs were omitted from the payload (render nothing, not a guess). */
export function reliabilityFormula(
  snapshot: ReliabilityFormulaSource,
): ReliabilityFormula | undefined {
  if (snapshot.invoices_paid === undefined || snapshot.invoices_paid_on_time === undefined) {
    return undefined;
  }
  return {
    onTime: snapshot.invoices_paid_on_time,
    paid: snapshot.invoices_paid,
    score: snapshot.score,
  };
}
